import {
  Command,
  HelpCircle,
  Info,
  Keyboard,
  MousePointer,
  Plus,
  Search,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

interface TipsPanelProps {
  className?: string
}

export const TipsPanel = ({ className }: TipsPanelProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const keyboardShortcuts = [
    { key: 'Enter', description: 'Confirmar edición' },
    { key: 'Esc', description: 'Cancelar edición' },
    { key: 'Tab', description: 'Navegar campos' },
    { key: 'Ctrl+S', description: 'Guardar cambios' },
  ]

  const tips = [
    {
      icon: MousePointer,
      text: 'Haz clic en un producto de la lista para encontrarlo en el inventario',
    },
    {
      icon: Search,
      text: 'Usa la búsqueda y filtros para encontrar productos rápidamente',
    },
    {
      icon: Plus,
      text: 'Usa los botones + y - para ajustar cantidades de forma granular',
    },
    {
      icon: Command,
      text: 'Los productos editados se muestran con un indicador visual',
    },
    // TODO: Add tip for saying that in the section "Productos nuevos" the price and the amount  can be edited
    {
      icon: Info,
      text: 'En la sección "Productos nuevos" puedes editar el precio y la cantidad de los productos',
    },
  ]

  if (!isOpen) {
    return (
      <div className={className}>
        <Button
          className="gap-2 text-muted-foreground hover:text-foreground"
          onClick={() => setIsOpen(true)}
          size="sm"
          variant="ghost"
        >
          <HelpCircle className="h-4 w-4" />
          Ayuda
        </Button>
      </div>
    )
  }

  return (
    <div className={cn('relative', className)}>
      <Card
        className="absolute right-0 top-8 z-50 w-80 border bg-background/95 shadow-lg backdrop-blur-sm"
        ref={cardRef}
      >
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            <CardTitle className="text-base font-medium text-primary">
              Ayuda & Atajos
            </CardTitle>
          </div>
          <Button
            className="h-6 w-6"
            onClick={() => setIsOpen(false)}
            size="icon"
            variant="ghost"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-5 pt-1">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Keyboard className="h-4 w-4 text-muted-foreground" />
              <h5 className="text-sm font-medium text-muted-foreground">
                Atajos de teclado
              </h5>
            </div>
            <div className="overflow-hidden rounded-md border">
              {keyboardShortcuts.map((shortcut, index) => (
                <div
                  className={cn(
                    'flex justify-between px-4 py-3 text-sm transition-colors hover:bg-muted/50',
                    index !== keyboardShortcuts.length - 1 ? 'border-b' : ''
                  )}
                  key={index}
                >
                  <span className="rounded bg-muted-foreground/50 px-2 py-0.5 font-mono text-xs font-semibold">
                    {shortcut.key}
                  </span>
                  <span className="text-muted-foreground">
                    {shortcut.description}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-muted-foreground" />
              <h5 className="text-sm font-medium text-muted-foreground">
                Consejos
              </h5>
            </div>
            <ul className="space-y-0 divide-y divide-border overflow-hidden rounded-md border">
              {tips.map((tip, index) => (
                <li
                  className="flex gap-2 p-3 transition-colors hover:bg-muted/50"
                  key={index}
                >
                  <tip.icon className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
                  <span className="text-sm text-muted-foreground">
                    {tip.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
